import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import dayjs from 'dayjs';
import { type MouseEvent, useMemo, useState } from 'react';

import {
  type CompanyDetailType,
  useWebTrafficActivityList,
  useWebTrafficActivityListCSVDownload,
} from '@/api/activity';
import { INTEGRATION_PROVIDER_ID } from '@/api/integration';
import { getProviderSquareLogoById } from '@/app/integrations/providerUtil';
import { LinkButton } from '@/components/buttons';
import { RangeFilter } from '@/components/filters';
import {
  InstantSearchDataTable,
  type InstantSearchDataTableColumnDef,
  useInstantSearchState,
} from '@/components/InstantSearch';
import { getSolQueryParamsNewFromQueryState } from '@/components/InstantSearch/util/search-util';
import { usePageFilterContext } from '@/components/page';
import { Panel } from '@/components/panels';
import { USDateViewFormat } from '@/constants';
import DataTableAdvancedFiltersDialog from '@/features/AdvancedFiltersModal/DataTableAdvancedFiltersDialog';
import { AudienceMembershipTags } from '@/features/AudienceMembershipTags';
import CompanyCell from '@/features/CompanyCell';
import { DataTableMetricSelector } from '@/features/DataTableMetricSelector';
import { FileDownloadDialog } from '@/features/FileDownloadModal';
import {
  AudienceFilter,
  CampaignFilter,
  ChannelFilter,
  InTamFilter,
  IndustryFilter,
  KeywordFilter,
  KeywordGroupFilter,
  PixelFilter,
  RegionFilter,
  RevenueRangeFilter,
  URLFilter,
  VendorFilter,
} from '@/features/filters';
import { numberFormat } from '@/helper/numberFormatter';
import { getRevenueRangeDescription } from '@/messages/revenue-range-name-messages';
import { TableState } from '@/providers/TableState';
import { ROUTES } from '@/router';

import ActivityActionMenu from './ActivityActionMenu';
import WebTrafficActivityDetailSheet from './WebTrafficActivityDetailSheet';
import { type ALL_WEB_TRAFFIC_FIELDS, getWebTrafficFieldName } from './webTrafficFields';

const ActivityOverviewTable = () => {
  const { queryState } = useInstantSearchState();
  const { pageFilters } = usePageFilterContext();
  const { startDate, endDate } = pageFilters;
  const [selectedRow, setSelectedRow] = useState<CompanyDetailType | null>(null);
  const [metricSelectorOpen, setMetricSelectorOpen] = useState(false);
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const { companies, totalResults, isLoading, error } = useWebTrafficActivityList(
    getSolQueryParamsNewFromQueryState(queryState),
    startDate,
    endDate,
  );

  const {
    isLoading: isDownloadingFile,
    beginDownload,
    cancelDownload,
  } = useWebTrafficActivityListCSVDownload(
    getSolQueryParamsNewFromQueryState(queryState),
    startDate,
    endDate,
  );

  // don't show "0 visits" if pixel filter is applied since pixels cannot have visits
  const pixelFilterId = 'pixelIds';
  const hideVisits = queryState.filters.some((filter) => filter.field === pixelFilterId);

  const sortFields = ROUTES.webTrafficActivity.searchParams['w.sortBy'].enumValues;

  const companyActivityMetrics = useMemo(
    () =>
      [
        {
          header: getWebTrafficFieldName('w.name'),
          id: 'name',
          enableSorting: sortFields.includes('name'),
          minSize: 150,
          flex: 1,
          metricSelector: false,
          cell: ({ row }) => <CompanyCell name={row.original.name} domain={row.original.tld} />,
        },
        {
          header: getWebTrafficFieldName('w.webActivityPagesList'),
          id: 'webActivityPagesList',
          hidden: true,
          filterComponent: (props) => <URLFilter {...props} />,
          infoPopover: (
            <Trans>
              URL/Page filtering includes folders and URL paths, but does not include domains or
              query parameters. Using domains, subdomains, or query parameters will not function as
              intended.
              <br />
              <br />
              Example:
              <br />
              "home/" will match: www.example.com/home/
            </Trans>
          ),
          advancedFilter: true,
          metricSelector: false,
        },
        {
          header: getWebTrafficFieldName('w.region.id'),
          id: 'region.id',
          minSize: 120,
          flex: 1,
          enableSorting: sortFields.includes('region.id'),
          filterComponent: (props) => <RegionFilter {...props} />,
          cell: ({ row }) => row.original.region?.name,
        },
        {
          header: getWebTrafficFieldName('w.sector.id'),
          id: 'sector.id',
          minSize: 110,
          flex: 1,
          enableSorting: sortFields.includes('sector.id'),
          filterComponent: (props) => <IndustryFilter {...props} />,
          cell: ({ row }) => row.original.sector?.name,
        },
        {
          header: getWebTrafficFieldName('w.revRange.id'),
          id: 'revRange.id',
          enableSorting: sortFields.includes('revRange.id'),
          minSize: 110,
          flex: 1,
          filterComponent: (props) => <RevenueRangeFilter {...props} />,
          cell: ({ row }) => getRevenueRangeDescription(row.original.revRange?.id),
        },
        {
          header: getWebTrafficFieldName('w.audienceIds'),
          id: 'audienceIds',
          size: 180,
          meta: { align: 'end' },
          filterComponent: (props) => <AudienceFilter {...props} />,
          cell: ({ row }) => (
            <AudienceMembershipTags
              audiences={row.original.audiences}
              isInTam={row.original.inTam}
            />
          ),
        },
        {
          header: getWebTrafficFieldName('w.inTam'),
          id: 'inTam',
          hidden: true,
          filterComponent: (props) => <InTamFilter {...props} />,
          advancedFilter: {
            title: t`TAM`,
          },
          metricSelector: false,
        },
        {
          header: getWebTrafficFieldName('w.channelIds'),
          id: 'channelIds',
          hidden: true,
          filterComponent: (props) => <ChannelFilter {...props} mode="single" hasExclude={false} />,
          advancedFilter: true,
          metricSelector: false,
        },
        {
          header: getWebTrafficFieldName('w.vendorIds'),
          id: 'vendorIds',
          hidden: true,
          filterComponent: (props) => <VendorFilter {...props} mode="single" hasExclude={false} />,
          advancedFilter: true,
          metricSelector: false,
        },
        {
          header: getWebTrafficFieldName('w.campaignIds'),
          id: 'campaignIds',
          hidden: true,
          filterComponent: (props) => (
            <CampaignFilter {...props} mode="multiple" hasExclude={false} />
          ),
          advancedFilter: true,
          metricSelector: false,
        },
        {
          header: getWebTrafficFieldName('w.adGroupIds'),
          id: 'adGroupIds',
          hidden: true,
          filterComponent: (props) => (
            <KeywordGroupFilter {...props} mode="multiple" hasExclude={false} />
          ),
          advancedFilter: true,
          metricSelector: false,
        },
        {
          header: getWebTrafficFieldName('w.keywordIds'),
          id: 'keywordIds',
          hidden: true,
          filterComponent: (props) => (
            <KeywordFilter {...props} mode="multiple" hasExclude={false} />
          ),
          advancedFilter: true,
          metricSelector: false,
        },
        {
          header: getWebTrafficFieldName('w.pixelIds'),
          id: pixelFilterId,
          hidden: true,
          filterComponent: (props) => <PixelFilter {...props} mode="multiple" hasExclude={false} />,
          advancedFilter: true,
          metricSelector: false,
        },
        {
          header: getWebTrafficFieldName('w.metrics.impressions'),
          id: 'metrics.impressions',
          enableSorting: sortFields.includes('metrics.impressions'),
          size: 120,
          meta: { align: 'end' },
          filterComponent: (props) => <RangeFilter {...props} />,
          cell: ({ row }) => numberFormat(row.original.metrics?.impressions),
        },
        {
          header: getWebTrafficFieldName('w.metrics.visits'),
          id: 'metrics.visits',
          enableSorting: sortFields.includes('metrics.visits'),
          meta: { align: 'end' },
          size: 90,
          filterComponent: (props) => <RangeFilter {...props} />,
          cell: ({ row }) => (hideVisits ? '-' : numberFormat(row.original.metrics?.visits)),
        },
        {
          header: getWebTrafficFieldName('w.metrics.lastActivityDate'),
          id: 'metrics.lastActivityDate',
          meta: { align: 'end' },
          enableSorting: sortFields.includes('metrics.lastActivityDate'),
          size: 85,
          cell: ({ row }) =>
            row.original.metrics?.lastActivityDate
              ? dayjs(row.original.metrics?.lastActivityDate).format(USDateViewFormat)
              : `\u2013`,
        },
        {
          header: '',
          id: 'actions',
          meta: { align: 'end' },
          size: 40,
          enableSorting: false,
          metricSelector: false,
          cell: ({ row }) => (
            <div className="-my-2">
              <LinkButton
                variant="ghost"
                size="icon"
                to={`https://www.linkedin.com/search/results/companies?keywords=${row.original.name ?? row.original.tld}`}
                target="_blank"
                onClick={(e: MouseEvent<HTMLElement>) => e.stopPropagation()}
              >
                {getProviderSquareLogoById(INTEGRATION_PROVIDER_ID.LINKEDIN)}
              </LinkButton>
            </div>
          ),
        },
      ] satisfies InstantSearchDataTableColumnDef<CompanyDetailType>[],
    [hideVisits, sortFields],
  );

  return (
    <TableState tableKey="companyActivity" columns={companyActivityMetrics}>
      <Panel
        title={<Trans>Company Activity</Trans>}
        actions={
          <ActivityActionMenu
            totalResults={totalResults}
            isLoading={isLoading}
            isDownloadingCSV={isDownloadingFile}
            onDownloadCSV={beginDownload}
            onOpenMetricSelector={() => setMetricSelectorOpen(true)}
            onOpenAdvancedFilters={() => setAdvancedFiltersOpen(true)}
          />
        }
        isFullHeight
      >
        <InstantSearchDataTable<CompanyDetailType>
          columns={companyActivityMetrics}
          data={companies}
          totalResults={totalResults}
          loading={isLoading}
          error={error}
          emptyMessage={<Trans>No web traffic activity found.</Trans>}
          initialColumnPinning={{
            left: ['name'],
          }}
          rowCursor="pointer"
          getRowId={(record) => record.id}
          getFieldName={(fieldId: string) =>
            getWebTrafficFieldName(`w.${fieldId}` as ALL_WEB_TRAFFIC_FIELDS)
          }
          onRow={(record) => ({
            onClick: () => setSelectedRow?.(record),
          })}
        />
        <WebTrafficActivityDetailSheet
          data={selectedRow}
          startDate={startDate}
          endDate={endDate}
          onClose={() => setSelectedRow(null)}
        />
        <FileDownloadDialog open={isDownloadingFile} onAbortDownload={cancelDownload} />
        <DataTableMetricSelector
          open={metricSelectorOpen}
          onClose={() => setMetricSelectorOpen(false)}
        />
        <DataTableAdvancedFiltersDialog
          open={advancedFiltersOpen}
          onClose={() => setAdvancedFiltersOpen(false)}
        />
      </Panel>
    </TableState>
  );
};

export default ActivityOverviewTable;
